// A mind map board's ideas: one tree of entries (t "node") around a root idea, the board's topic. What the
// browser and the server both need: the tree read in order, the automatic layout (balanced left and right,
// each idea's ideas in a column beside it), the tree as an indented Markdown outline, and an agent's edits
// worked out as entry ops, all or nothing.

import { childrenOf, descendants, orderAt } from "./entries";
import type { Entry, ID, Op } from "./types";
import { cleanPictureRef, picturePath, type PictureRef } from "./imageInfo";

/**
 * An idea's own fields. side and color are an idea on the root's: the side it sits and its branch's chart
 * color (1 to 5), each chosen when it is made and kept, so a branch never jumps sides or changes color.
 */
export interface NodeData {
  text: string;
  folded: boolean;
  side: "left" | "right" | null;
  color?: number | null;
  /** connectors drawn from this idea to others anywhere on the map, by id (David, 15 Oct); absent when none */
  links?: ID[];
  /** a picture the board keeps, shown in the idea above its words (Features 2, 21 Oct); absent when none */
  image?: PictureRef;
}

/** How many connectors one idea may start. */
export const MAX_LINKS = 12;

/** An idea's connectors, as kept: ids, each once, never itself, within MAX_LINKS. */
export function cleanLinks(raw: unknown, self?: ID): ID[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((x): x is string => typeof x === "string" && /^[A-Za-z0-9_-]{4,40}$/.test(x) && x !== self))].slice(0, MAX_LINKS);
}

/** Every connector between two ideas that are both on the map, each pair once, as [from, to]. */
export function connectorsOf<E extends Pick<Entry, "id" | "data">>(nodes: E[]): [ID, ID][] {
  const here = new Set(nodes.map((e) => e.id));
  const seen = new Set<string>();
  const out: [ID, ID][] = [];
  for (const e of nodes)
    for (const to of cleanLinks(e.data.links, e.id)) {
      const k = [e.id, to].sort().join("|");
      if (!here.has(to) || seen.has(k)) continue;
      seen.add(k);
      out.push([e.id, to]);
    }
  return out;
}

/** How long an idea may be: a phrase or a sentence, never a page. */
export const MAX_IDEA = 280;

/** An idea's words as kept: one line, trimmed, within MAX_IDEA. */
export function cleanIdea(raw: unknown): string {
  return typeof raw === "string" ? raw.replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ").trim().slice(0, MAX_IDEA) : "";
}

export function ideaText(e: Pick<Entry, "data"> | null | undefined): string {
  const t = typeof e?.data?.text === "string" ? e.data.text.trim() : "";
  return t || "Untitled idea";
}

export const isFolded = (e: Pick<Entry, "data">) => e.data.folded === true;

/** A board's ideas, from all its entries. */
export function nodesOf<E extends Pick<Entry, "t">>(entries: E[]): E[] {
  return entries.filter((e) => e.t === "node");
}

/** The root idea: the one at the top (the first made, should there ever be two). */
export function rootOf<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt">>(nodes: E[]): E | null {
  return childrenOf(nodes, null).sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1))[0] ?? null;
}

/** The ideas from the root down to one, the root first, the idea itself last. */
export function trailOf<E extends Pick<Entry, "id" | "parent">>(nodes: E[], id: ID): E[] {
  const byId = new Map(nodes.map((e) => [e.id, e]));
  const out: E[] = [];
  for (let e = byId.get(id); e && out.length < 64; e = e.parent ? byId.get(e.parent) : undefined) out.unshift(e);
  return out;
}

/** The branch an idea is on: the root's idea it sits under (or itself), null for the root. */
export function branchOf<E extends Pick<Entry, "id" | "parent">>(nodes: E[], id: ID): E | null {
  const t = trailOf(nodes, id);
  return t.length > 1 ? t[1] : null;
}

const colorOf = (e: Pick<Entry, "data">): number | null => (typeof e.data.color === "number" && e.data.color >= 1 && e.data.color <= 5 ? Math.round(e.data.color) : null);

/**
 * Each branch's color, 1 to 5 (the theme's chart colors): the one it keeps, or, for one made without, the
 * next in turn by when it was made. A branch keeps its color when ideas around it come and go.
 */
export function branchColors<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[]): Map<ID, number> {
  const root = rootOf(nodes);
  const out = new Map<ID, number>();
  if (!root) return out;
  const made = childrenOf(nodes, root.id).sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  made.forEach((e, i) => out.set(e.id, colorOf(e) ?? (i % 5) + 1));
  return out;
}

/** The color a new branch on the root takes: the one its branches use least, the first of those. */
export function newColor<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], root: ID): number {
  const used = [0, 0, 0, 0, 0];
  for (const c of branchColors(nodes.filter((e) => e.parent === root || !e.parent)).values()) used[c - 1]++;
  return used.indexOf(Math.min(...used)) + 1;
}

const sideOf = (e: Pick<Entry, "data">): "left" | "right" | null => (e.data.side === "left" || e.data.side === "right" ? e.data.side : null);

/** How much room an idea's branch takes: the ideas showing at its ends. */
function weight<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], id: ID, open: (e: E) => boolean): number {
  const e = nodes.find((x) => x.id === id);
  const kids = e && open(e) ? childrenOf(nodes, id) : [];
  return kids.length ? kids.reduce((n, k) => n + weight(nodes, k.id, open), 0) : 1;
}

/**
 * Which side each of the root's ideas sits on: the side it keeps (data.side), or, for one without, the
 * lighter side as they are read in order. Balanced, and calm: an idea added at the end never moves another.
 */
export function sidesOf<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], under: ID, keep = true, open: (e: E) => boolean = (e) => !isFolded(e)): Map<ID, 1 | -1> {
  const out = new Map<ID, 1 | -1>();
  let right = 0;
  let left = 0;
  for (const k of childrenOf(nodes, under)) {
    const kept = keep ? sideOf(k) : null;
    const s: 1 | -1 = kept ? (kept === "right" ? 1 : -1) : right <= left ? 1 : -1;
    out.set(k.id, s);
    if (s === 1) right += weight(nodes, k.id, open);
    else left += weight(nodes, k.id, open);
  }
  return out;
}

/** The side a new idea on the root goes: the lighter one, the right on a tie. */
export function newSide<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], root: ID): "left" | "right" {
  const sides = sidesOf(nodes, root);
  let right = 0;
  let left = 0;
  for (const [id, s] of sides) {
    const w = weight(nodes, id, (e) => !isFolded(e));
    if (s === 1) right += w;
    else left += w;
  }
  return right <= left ? "right" : "left";
}

// ---------------------------------------------------------------------------
// layout

export interface Size {
  w: number;
  h: number;
}

export interface MapBox extends Size {
  id: ID;
  /** the middle of the idea, in the board's own units, the root at 0, 0 */
  x: number;
  y: number;
  /** 0 for the root (or the idea in focus) */
  depth: number;
  /** which way its ideas grow: 1 to the right, -1 to the left, 0 for the root */
  side: 1 | -1 | 0;
  /** its branch's chart color, 1 to 5; 0 for the root */
  color: number;
  parent: ID | null;
  /** how many ideas are folded away inside it */
  hidden: number;
}

export interface MapLayout {
  boxes: Map<ID, MapBox>;
  /** the idea drawn in the middle: the root, or the one in focus */
  center: ID | null;
  bounds: { x: number; y: number; w: number; h: number } | null;
}

/** Room between an idea and the column of its ideas, by its depth. */
const H_GAP = [72, 48, 40];
/** Room between ideas side by side in a column, by their depth. */
const V_GAP = [0, 26, 10, 7];

const hGap = (depth: number) => H_GAP[Math.min(depth, H_GAP.length - 1)];
const vGap = (depth: number) => V_GAP[Math.min(depth, V_GAP.length - 1)];

/**
 * Where every showing idea goes. Each idea's ideas sit in a column beside it, lined up on the side nearest
 * it and centred on it as a block; the root's ideas split left and right (sidesOf). A folded idea hides its
 * ideas and says how many. With focus, the idea in focus takes the middle and its ideas split around it.
 */
export function layoutMap<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], size: (e: E, depth: number) => Size, opts: { focus?: ID | null } = {}): MapLayout {
  const boxes = new Map<ID, MapBox>();
  const root = rootOf(nodes);
  const center = (opts.focus && nodes.find((e) => e.id === opts.focus)) || root;
  if (!center) return { boxes, center: null, bounds: null };
  const kidsOf = new Map<ID | null, E[]>();
  for (const e of nodes) {
    const k = e.parent ?? null;
    if (!kidsOf.has(k)) kidsOf.set(k, []);
    kidsOf.get(k)!.push(e);
  }
  for (const list of kidsOf.values()) list.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  const colors = branchColors(nodes);
  // the middle idea shows its ideas even when folded: it is the one being looked at
  const open = (e: E) => e.id === center.id || !isFolded(e);
  const kids = (e: E) => (open(e) ? (kidsOf.get(e.id) ?? []) : []);
  const sizes = new Map<ID, Size>();
  const sz = (e: E, depth: number) => {
    let s = sizes.get(e.id);
    if (!s) sizes.set(e.id, (s = size(e, depth)));
    return s;
  };
  const tall = new Map<ID, number>();
  const height = (e: E, depth: number): number => {
    const ks = kids(e);
    const own = sz(e, depth).h;
    const h = ks.length ? Math.max(own, ks.reduce((n, k) => n + height(k, depth + 1), 0) + vGap(depth + 1) * (ks.length - 1)) : own;
    tall.set(e.id, h);
    return h;
  };
  const count = (id: ID): number => (kidsOf.get(id) ?? []).reduce((n, k) => n + 1 + count(k.id), 0);
  const column = (list: E[], parent: MapBox, side: 1 | -1, depth: number) => {
    if (!list.length) return;
    const total = list.reduce((n, k) => n + tall.get(k.id)!, 0) + vGap(depth) * (list.length - 1);
    let top = parent.y - total / 2;
    const edge = parent.x + side * (parent.w / 2 + hGap(parent.depth));
    for (const k of list) {
      const s = sz(k, depth);
      const h = tall.get(k.id)!;
      place(k, edge + (side * s.w) / 2, top + h / 2, depth, side, parent.depth === 0 ? (colors.get(k.id) ?? 1) : parent.color);
      top += h + vGap(depth);
    }
  };
  const place = (e: E, x: number, y: number, depth: number, side: 1 | -1, color: number) => {
    const s = sz(e, depth);
    const box: MapBox = { id: e.id, x, y, w: s.w, h: s.h, depth, side, color, parent: e.parent, hidden: open(e) ? 0 : count(e.id) };
    boxes.set(e.id, box);
    column(kids(e), box, side, depth + 1);
  };
  // the middle
  const s0 = sz(center, 0);
  const mid: MapBox = { id: center.id, x: 0, y: 0, w: s0.w, h: s0.h, depth: 0, side: 0, color: center.id === root?.id ? 0 : (colors.get(branchOf(nodes, center.id)?.id ?? "") ?? 0), parent: center.parent, hidden: 0 };
  boxes.set(center.id, mid);
  const first = kids(center);
  for (const k of first) height(k, 1);
  // the root's ideas keep their sides; around an idea in focus they are balanced afresh
  const sides = sidesOf(nodes, center.id, center.id === root?.id, open);
  for (const side of [1, -1] as const) column(first.filter((k) => sides.get(k.id) === side), mid, side, 1);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const b of boxes.values()) {
    x0 = Math.min(x0, b.x - b.w / 2);
    y0 = Math.min(y0, b.y - b.h / 2);
    x1 = Math.max(x1, b.x + b.w / 2);
    y1 = Math.max(y1, b.y + b.h / 2);
  }
  return { boxes, center: center.id, bounds: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } };
}

/**
 * An idea's size without a browser to measure it, from its words: for where an agent's widget goes beside
 * the map, and for the map in small in All boards. The browser measures the real thing.
 */
export function guessSize(text: string, depth: number): Size {
  const [px, padX, padY, line, max, extra] = depth === 0 ? [22, 48, 30, 28, 380, 0] : depth === 1 ? [14.5, 28, 16, 20, 280, 15] : [13, 18, 8, 18, 260, 0];
  const width = text.length * px * 0.54;
  const inner = max - padX - extra;
  const lines = Math.max(1, Math.ceil(width / inner));
  return { w: Math.round(Math.min(max, width + padX + extra + 2)), h: Math.round(lines * line + padY) };
}

// ---------------------------------------------------------------------------
// Markdown

/**
 * The tree as an indented Markdown outline: the root as a heading, every idea as a list item under the one
 * it sits in. ids adds each idea's id (and says which are folded), for agents; an export leaves them out.
 */
export function toOutline<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt" | "data">>(nodes: E[], opts: { ids?: boolean; from?: ID | null } = {}): string {
  const root = rootOf(nodes);
  const top = (opts.from && nodes.find((e) => e.id === opts.from)) || root;
  if (!top) return "";
  const tag = (e: E) => {
    if (!opts.ids) return "";
    const n = childrenOf(nodes, e.id).length;
    const pic = cleanPictureRef(e.data.image);
    return ` (id ${e.id}${isFolded(e) && n ? `, folded, ${n} inside` : ""}${pic ? `, picture ${picturePath(pic.key)}` : ""})`;
  };
  const lines = [`# ${ideaText(top)}${tag(top)}`, ""];
  const walk = (id: ID, depth: number) => {
    for (const k of childrenOf(nodes, id)) {
      lines.push(`${"  ".repeat(depth)}- ${ideaText(k)}${tag(k)}`);
      if (depth < 64) walk(k.id, depth + 1);
    }
  };
  walk(top.id, 0);
  if (lines.length === 2) lines.pop();
  return lines.join("\n");
}

export interface OutlineIdea {
  text: string;
  kids: OutlineIdea[];
}

/**
 * Ideas from an indented list: "- " (or "*", "+", "1.") items, each indented under the one it belongs in,
 * by two spaces, four or a tab. A line with no bullet is an idea too. A heading's # and a to-do's box go.
 */
export function parseOutline(md: string): OutlineIdea[] {
  const out: OutlineIdea[] = [];
  const stack: { indent: number; idea: OutlineIdea }[] = [];
  for (const raw of md.replace(/\r/g, "").split("\n")) {
    if (!raw.trim()) continue;
    const indent = raw.replace(/\t/g, "    ").match(/^ */)![0].length;
    const text = cleanIdea(
      raw
        .trim()
        .replace(/^#{1,6}\s+/, "")
        .replace(/^([-*+]|\d+[.)])\s+/, "")
        .replace(/^\[[ xX]\]\s+/, ""),
    );
    if (!text) continue;
    const idea: OutlineIdea = { text, kids: [] };
    while (stack.length && stack[stack.length - 1].indent >= indent) stack.pop();
    if (stack.length) stack[stack.length - 1].idea.kids.push(idea);
    else out.push(idea);
    stack.push({ indent, idea });
  }
  return out;
}

const count = (list: OutlineIdea[]): number => list.reduce((n, i) => n + 1 + count(i.kids), 0);

// ---------------------------------------------------------------------------
// an agent's edits

export type MapAction = "add" | "rename" | "move" | "fold" | "unfold" | "remove" | "connect" | "disconnect";
export const MAP_ACTIONS: MapAction[] = ["add", "rename", "move", "fold", "unfold", "remove", "connect", "disconnect"];

export interface MapChange {
  action: MapAction;
  /** the idea it acts on: its id, or its exact words */
  idea?: string;
  /** add and move: the idea it goes under */
  under?: string;
  /** add and move: the idea beside it to go after */
  after?: string;
  /** add: the new idea, or an indented "- " list of several; rename: the new words */
  text?: string;
  /** connect and disconnect: the other idea */
  to?: string;
}

export interface MapPlan {
  ops: Op[];
  /** what was done, a line a change, in words an agent reads */
  done: string[];
  /** the ideas made, in order */
  added: { id: ID; text: string; under: ID }[];
  removed: { id: ID; text: string; inside: number }[];
  /** each idea an op puts or removes, by the change (from 1) that last touched it, so a refusal names it */
  changeOf: Record<ID, number>;
}

export class MapRefused extends Error {}

/**
 * An agent's changes to a map, as entry ops on a working copy: each change sees the ones before it, and
 * the first that cannot be done refuses them all, saying which and why. newId makes each new idea's id.
 */
export function planMapEdit(entries: Entry[], boardId: ID, changes: MapChange[], ctx: { now: number; newId: () => ID; max?: number }): MapPlan {
  const nodes: Entry[] = nodesOf(entries).map((e) => ({ ...e, data: { ...e.data } }));
  const root = rootOf(nodes);
  if (!root) throw new MapRefused("This map has no root idea.");
  const touched = new Map<ID, Entry>();
  const gone = new Set<ID>();
  const plan: MapPlan = { ops: [], done: [], added: [], removed: [], changeOf: {} };
  let now = 0;
  const live = () => nodes.filter((e) => !gone.has(e.id));
  const name = (e: Entry) => `"${ideaText(e)}" (${e.id})`;
  const find = (ref: string | undefined, what: string): Entry => {
    const want = String(ref ?? "").trim();
    if (!want) throw new MapRefused(`${what} is an idea's id or its exact words; read_map lists them.`);
    const all = live();
    const byId = all.find((e) => e.id === want);
    if (byId) return byId;
    const low = want.toLowerCase();
    let hits = all.filter((e) => ideaText(e).toLowerCase() === low);
    if (!hits.length) hits = all.filter((e) => ideaText(e).toLowerCase().includes(low));
    if (hits.length === 1) return hits[0];
    if (hits.length) throw new MapRefused(`"${want}" names ${hits.length} ideas: ${hits.slice(0, 6).map(name).join(", ")}. Use the id.`);
    throw new MapRefused(`No idea called "${want}" on the map. read_map lists them with their ids.`);
  };
  const put = (e: Entry) => {
    const i = nodes.findIndex((x) => x.id === e.id);
    if (i < 0) nodes.push(e);
    else nodes[i] = e;
    touched.set(e.id, e);
    plan.changeOf[e.id] = now;
  };
  const orderAfter = (parent: ID, after: Entry | null, moving?: ID) => {
    const sibs = childrenOf(live(), parent).filter((e) => e.id !== moving);
    if (!after) return orderAt(sibs, sibs.length);
    if (after.parent !== parent) throw new MapRefused(`"${ideaText(after)}" is not under the idea it would go beside.`);
    return orderAt(sibs, sibs.findIndex((e) => e.id === after.id) + 1);
  };
  const limit = ctx.max ?? 2000;
  changes.forEach((c, n) => {
    now = n + 1;
    const at = changes.length > 1 ? `Change ${n + 1}: ` : "";
    try {
      if (!c || typeof c !== "object" || !MAP_ACTIONS.includes(c.action)) throw new MapRefused(`action is one of ${MAP_ACTIONS.join(", ")}.`);
      switch (c.action) {
        case "add": {
          const under = c.under ? find(c.under, "under") : root;
          const list = parseOutline(String(c.text ?? ""));
          if (!list.length) throw new MapRefused("add needs text: the idea's words, or an indented - list for several.");
          if (live().length + count(list) > limit) throw new MapRefused(`A map holds up to ${limit} ideas, and this would make ${live().length + count(list)}.`);
          if (trailOf(live(), under.id).length + depthOf(list) > 13) throw new MapRefused("Ideas go up to 12 deep.");
          let after = c.after ? find(c.after, "after") : null;
          const made: Entry[] = [];
          const add = (idea: OutlineIdea, parent: Entry, prev: Entry | null) => {
            const onRoot = parent.id === root.id;
            const e: Entry = { id: ctx.newId(), boardId, t: "node", parent: parent.id, order: orderAfter(parent.id, prev), data: { text: idea.text, folded: false, side: onRoot ? newSide(live(), root.id) : null, color: onRoot ? newColor(live(), root.id) : null }, createdAt: ctx.now, updatedAt: ctx.now, by: null };
            put(e);
            made.push(e);
            plan.added.push({ id: e.id, text: idea.text, under: parent.id });
            let last: Entry | null = null;
            for (const k of idea.kids) last = add(k, e, last);
            return e;
          };
          for (const idea of list) after = add(idea, under, after);
          // the idea they went under shows them
          if (isFolded(under)) put({ ...under, data: { ...under.data, folded: false } });
          plan.done.push(made.length === 1 ? `Added the idea ${name(made[0])} under "${ideaText(under)}".` : `Added ${made.length} ideas under "${ideaText(under)}": ${made.slice(0, 20).map(name).join(", ")}${made.length > 20 ? ", and more" : ""}.`);
          break;
        }
        case "rename": {
          const e = find(c.idea, "idea");
          const text = cleanIdea(c.text);
          if (!text) throw new MapRefused("rename needs text, the idea's new words.");
          const was = ideaText(e);
          put({ ...e, data: { ...e.data, text } });
          plan.done.push(`Renamed "${was}" to "${text}" (${e.id}).`);
          break;
        }
        case "move": {
          const e = find(c.idea, "idea");
          if (e.id === root.id) throw new MapRefused("The root idea stays in the middle; rename it instead.");
          const under = c.under ? find(c.under, "under") : root;
          if (under.id === e.id || descendants(live(), e.id).some((d) => d.id === under.id)) throw new MapRefused(`"${ideaText(e)}" cannot go inside itself or an idea inside it.`);
          const after = c.after ? find(c.after, "after") : null;
          const stays = under.id === root.id && e.parent === root.id;
          const others = live().filter((x) => x.id !== e.id);
          const side = under.id === root.id ? (stays && e.data.side ? (e.data.side as "left" | "right") : newSide(others, root.id)) : null;
          const color = under.id === root.id ? (stays && colorOf(e) ? colorOf(e) : newColor(others, root.id)) : null;
          put({ ...e, parent: under.id, order: orderAfter(under.id, after, e.id), data: { ...e.data, side, color } });
          plan.done.push(`Moved ${name(e)} under "${ideaText(under)}".`);
          break;
        }
        case "fold":
        case "unfold": {
          const e = find(c.idea, "idea");
          if (e.id === root.id) throw new MapRefused("The root idea does not fold; fold the ideas on it.");
          put({ ...e, data: { ...e.data, folded: c.action === "fold" } });
          plan.done.push(`${c.action === "fold" ? "Folded" : "Unfolded"} ${name(e)}.`);
          break;
        }
        case "connect":
        case "disconnect": {
          const e = find(c.idea, "idea");
          const to = find(c.to, "to");
          if (e.id === to.id) throw new MapRefused("A connector joins two different ideas.");
          const linked = (a: Entry, b: Entry) => cleanLinks(a.data.links, a.id).includes(b.id);
          if (c.action === "connect") {
            if (linked(e, to) || linked(to, e)) {
              plan.done.push(`${name(e)} and ${name(to)} were connected already.`);
              break;
            }
            const links = cleanLinks(e.data.links, e.id);
            if (links.length >= MAX_LINKS) throw new MapRefused(`"${ideaText(e)}" has ${MAX_LINKS} connectors, the most one idea starts.`);
            put({ ...e, data: { ...e.data, links: [...links, to.id] } });
            plan.done.push(`Connected ${name(e)} to ${name(to)}.`);
          } else {
            if (!linked(e, to) && !linked(to, e)) throw new MapRefused(`"${ideaText(e)}" and "${ideaText(to)}" are not connected.`);
            for (const [a, b] of [[e, to], [to, e]] as const)
              if (linked(a, b)) {
                // an empty list, not a missing one: the server keeps an idea's connectors when the field is left out
                put({ ...a, data: { ...a.data, links: cleanLinks(a.data.links, a.id).filter((x) => x !== b.id) } });
              }
            plan.done.push(`Took away the connector between ${name(e)} and ${name(to)}.`);
          }
          break;
        }
        case "remove": {
          const e = find(c.idea, "idea");
          if (e.id === root.id) throw new MapRefused("The root idea cannot be removed; rename it instead.");
          const inside = descendants(live(), e.id);
          for (const d of [e, ...inside]) {
            gone.add(d.id);
            plan.changeOf[d.id] = now;
          }
          plan.removed.push({ id: e.id, text: ideaText(e), inside: inside.length });
          plan.done.push(`Moved ${name(e)}${inside.length ? ` and the ${inside.length === 1 ? "idea" : `${inside.length} ideas`} inside it` : ""} to the trash.`);
          break;
        }
      }
    } catch (err) {
      if (err instanceof MapRefused) throw new MapRefused(`${at}${err.message} Nothing was changed.`);
      throw err;
    }
  });
  // what was made and then removed in the same call was never there. The rest go in shallowest first, so each idea's
  // place is there before it: an idea made in this call, moved under one made after it, went in first and was refused,
  // and was lost while the other changes stayed (Fixes 9, 28 Oct)
  const depth = new Map<ID, number>();
  const depthIn = (id: ID): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    depth.set(id, 0);
    const e = nodes.find((x) => x.id === id);
    const d = e?.parent ? depthIn(e.parent) + 1 : 0;
    depth.set(id, d);
    return d;
  };
  const puts = [...touched.values()].filter((e) => !gone.has(e.id));
  for (const e of puts.map((x, i) => ({ x, i, d: depthIn(x.id) })).sort((a, b) => a.d - b.d || a.i - b.i)) plan.ops.push({ op: "entry.put", entry: e.x });
  // removed: every idea that was there before, each with the ones inside it where it stands now, unless the one it sits
  // in goes too. An idea moved under one that is then removed is not put, so it sits where it was and goes on its own
  const was = new Map(nodesOf(entries).map((e) => [e.id, e]));
  const binned = new Set([...gone].filter((id) => was.has(id)));
  for (const id of binned) if (!binned.has(was.get(id)!.parent ?? "")) plan.ops.push({ op: "entry.del", id });
  return plan;
}

function depthOf(list: OutlineIdea[]): number {
  return list.length ? 1 + Math.max(...list.map((i) => depthOf(i.kids))) : 0;
}
